/* EDITING A MEMBER. Until now the product could not.
 *
 * Every screen READ a member and nothing could change one — not a corrected phone
 * number, not a plan, not why they joined. On a real gym that is a record that drifts
 * out of date from the day it is imported and can never be brought back, which makes
 * every screen built on it slowly less true.
 *
 * ── WHAT A SEAT MAY CHANGE (Anuj, 2026-09-15: "yes, they see what their permissions
 * let them see") ─────────────────────────────────────────────────────────────────
 *
 *   commercials OFF                no plan, price or renewal field AT ALL. Not
 *                                  disabled, not dashes — absent. A greyed-out price
 *                                  box tells a coach exactly what the rule exists to
 *                                  stop them knowing: that there is a price, and
 *                                  roughly where to look for it.
 *   commercials ON, member_edit OFF  the fields are there and editable, and saving
 *                                  RAISES AN APPROVAL rather than writing. The seat
 *                                  is told that before they type, not after.
 *   member_edit ON                 writes directly.
 *
 * The source enforces this, not this screen. SeedSource.updateMember checks the same
 * permission against the same NEEDS_SIGN_OFF list and raises the approval itself, so
 * a seat that reached the API another way gets the same answer. What this file does
 * is make the rule VISIBLE — which fields exist, and what the button is about to do.
 *
 * ── AND WHY A SUSPENSION ASKS FOR A DATE (Anuj, same day) ────────────────────────
 *
 * "there should be requirement to place a time period on the suspension when changing
 * the membership type (then a day or two before the suspension) we should be prompted
 * to ask them (hows the injury, how was vacation, etc, based on the notes)"
 *
 * So the return date is REQUIRED, and so is the reason. A suspension with neither is
 * how somebody stops being a member without anybody deciding they should: they fall
 * off every list — not scored, not chased, not counted — and nothing ever brings them
 * back up. The date is what makes it a pause rather than a disappearance, and the
 * reason is what makes the conversation on the way back a human one instead of "your
 * membership resumes".
 */
import { useState } from 'react';
import type { Member, MemberStatus } from '@acrostic/model';
import { permissionsOf } from '@acrostic/model/permissions';
import { useAsync, useSource } from '../../data/SourceProvider.js';
import { Field, Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { gymToday } from '../../lib/today.js';

/** Money in, money out, without a float ever holding a price. */
const toCents = (s: string): number | null => {
  const t = s.trim().replace(/[^0-9.]/g, '');
  if (!t) return null;
  const n = Math.round(Number(t) * 100);
  return Number.isFinite(n) ? n : null;
};
const fromCents = (c: number | null | undefined): string =>
  c == null ? '' : (c / 100).toFixed(2);

/** N days after the gym's day, as an ISO date. Used for the default return date. */
function plusDays(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function MemberEdit({ m, canSeeCommercials, onClose, onDone }: {
  m: Member;
  canSeeCommercials: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const source = useSource();
  const toast = useToast();

  const [firstName, setFirstName] = useState(m.firstName ?? '');
  const [lastName, setLastName] = useState(m.lastName ?? '');
  const [email, setEmail] = useState(m.email ?? '');
  const [phone, setPhone] = useState(m.phone ?? '');
  const [why, setWhy] = useState(m.why ?? '');
  const [goal, setGoal] = useState(m.goal ?? '');

  const [status, setStatus] = useState<MemberStatus>(m.status);
  const [plan, setPlan] = useState(m.plan ?? '');
  const [price, setPrice] = useState(fromCents(m.priceCents));

  /* The suspension block. Pre-filled from an existing hold so re-opening the form on
     somebody already suspended shows what was agreed rather than an empty box. */
  const held = m.suppression?.kind === 'hold' ? m.suppression : null;
  const [until, setUntil] = useState(held?.reviewAt ?? '');
  const [reason, setReason] = useState(held?.reason ?? '');
  const [busy, setBusy] = useState(false);

  const suspending = status === 'suspended';
  const wasSuspended = m.status === 'suspended';

  /* WHAT SAVING WILL ACTUALLY DO, so the button can say it before it is pressed.
     The SOURCE decides for real — this only has to agree with it, and it agrees by
     asking the same function about the same permission. A seat that will raise an
     approval should learn that while typing, not from a toast afterwards. */
  const session = useAsync(() => source.getSession(), [source]);
  const mine = permissionsOf(session.data?.user.roles, session.data?.user.permissions ?? null);
  const writesDirectly = mine.includes('member_edit');

  /* The fields the source will hold back for sign-off. Kept in the same order and
     the same words as SeedSource.NEEDS_SIGN_OFF so the two can be read together. */
  const gated: string[] = [];
  if (status !== m.status) gated.push('membership status');
  if (canSeeCommercials) {
    if (plan.trim() !== (m.plan ?? '')) gated.push('plan');
    if (toCents(price) !== (m.priceCents ?? null)) gated.push('price');
  }
  const willRaiseApproval = !writesDirectly && gated.length > 0;

  async function save() {
    const first = firstName.trim();
    if (!first) { toast('A member needs a first name', 'bad'); return; }

    if (suspending) {
      if (!until) {
        toast('A suspension needs a date to come back on. Without one they just disappear.', 'bad');
        return;
      }
      if (until <= gymToday()) {
        toast('The return date has to be in the future', 'bad');
        return;
      }
      if (!reason.trim()) {
        toast('Say why, in your own words: it is what you will ask them about.', 'bad');
        return;
      }
    }

    const patch: Partial<Member> = {
      firstName: first,
      lastName: lastName.trim(),
      name: `${first} ${lastName.trim()}`.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      why: why.trim() || null,
      goal: goal.trim() || null,
    };

    if (status !== m.status) patch.status = status;

    /* A SUSPENSION IS TWO FACTS, and they have to move together. `status` is the
       membership; `suppression` is the promise to come back to them, and it is the
       half that holds the date and the reason. Writing one without the other is how
       a member ends up unscored with nothing scheduled — SITE-RULES 1's exact
       failure mode, where suppression quietly becomes abandonment. */
    if (suspending) {
      patch.suppression = {
        kind: 'hold',
        reason: reason.trim(),
        declaredAt: gymToday(),
        /* WHO DECIDED. A hold with no name on it is one nobody owns, and the
           review list has to be able to say who to ask about it. */
        declaredBy: session.data?.user.name ?? held?.declaredBy ?? null,
        reviewAt: until,
      };
    } else if (wasSuspended && held) {
      patch.suppression = null;          // back in the room, and scored again
    }

    if (canSeeCommercials) {
      if (plan.trim() !== (m.plan ?? '')) patch.plan = plan.trim() || null;
      const cents = toCents(price);
      if (cents !== (m.priceCents ?? null)) patch.priceCents = cents;
    }

    setBusy(true);
    try {
      await source.updateMember(m.id, patch);
      toast(willRaiseApproval
        ? `Sent for sign-off: ${gated.join(', ')}`
        : `Saved ${first}`, 'good');
      onDone();
    } catch (e) {
      toast((e as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Edit ${m.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn primary t-label-medium"
            disabled={busy} onClick={() => void save()}
          >{willRaiseApproval ? 'Send for sign-off' : 'Save'}</button>
        </>
      }
    >
      {/* SAID BEFORE THEY TYPE, and it names the fields rather than the rule. A seat
          that cannot sign off its own change is not being refused — the change is
          being sent to somebody who can. */}
      {willRaiseApproval && (
        <p className="t-body-small notice">
          You can change {gated.join(' and ')}, but not sign it off. Saving sends
          {' '}{gated.length > 1 ? 'those' : 'that'} to an Owner or Manager and applies
          everything else now.
        </p>
      )}

      <div className="formrow">
        <Field label="First name">
          <input className="field" value={firstName} autoFocus
            onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field label="Last name">
          <input className="field" value={lastName}
            onChange={(e) => setLastName(e.target.value)} />
        </Field>
      </div>

      <div className="formrow">
        <Field label="Email">
          <input className="field" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Phone">
          <input className="field" value={phone}
            onChange={(e) => setPhone(e.target.value)} />
        </Field>
      </div>

      {/* THE FIELD THE PRODUCT IS ABOUT, so it is not filed under "other". A record
          with a why is a record somebody can have a conversation from. */}
      <Field
        label="Why they are here"
        hint="In their words, not a category. This is the one the floor actually reads."
      >
        <textarea className="field" rows={2} value={why}
          placeholder="Wants to keep up with her kids on the hill walk in June."
          onChange={(e) => setWhy(e.target.value)} />
      </Field>

      <Field label="Goal">
        <input className="field" value={goal}
          onChange={(e) => setGoal(e.target.value)} />
      </Field>

      <Field label="Membership">
        <select className="field" value={status}
          onChange={(e) => setStatus(e.target.value as MemberStatus)}>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </Field>

      {/* THE PERIOD, required, and only asked when it is relevant. */}
      {suspending && (
        <div className="subform">
          <p className="t-body-small muted">
            A suspension is a pause, so it needs an end. A day or two before this date
            {' '}{m.firstName} comes back onto the floor&rsquo;s list, with the reason
            below, so somebody asks about it rather than just reactivating them.
          </p>
          <div className="formrow">
            <Field label="Back on">
              <input
                className="field" type="date" value={until}
                min={plusDays(gymToday(), 1)}
                onChange={(e) => setUntil(e.target.value)}
              />
            </Field>
            <Field label="">
              <div className="chiprow">
                {[[30, '1 month'], [60, '2 months'], [90, '3 months']].map(([n, label]) => (
                  <button
                    key={String(n)} type="button" className="btn t-label-small"
                    onClick={() => setUntil(plusDays(gymToday(), n as number))}
                  >{label}</button>
                ))}
              </div>
            </Field>
          </div>
          <Field
            label="Why, in your own words"
            hint="What you will ask them about when they come back. “Knee surgery, out six weeks” beats “medical”."
          >
            <textarea className="field" rows={2} value={reason}
              placeholder="Knee surgery on the 20th. Back to light work after six weeks."
              onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
      )}

      {wasSuspended && !suspending && (
        <p className="t-body-small muted">
          Taking {m.firstName} off hold. They start being scored again from today, on
          the rhythm they had before.
        </p>
      )}

      {/* ABSENT, NOT DISABLED, when the seat may not see money. */}
      {canSeeCommercials && (
        <div className="formrow">
          <Field label="Plan">
            <input className="field" value={plan}
              onChange={(e) => setPlan(e.target.value)} />
          </Field>
          <Field label="Price" hint="Per month.">
            <input className="field" inputMode="decimal" value={price}
              onChange={(e) => setPrice(e.target.value)} />
          </Field>
        </div>
      )}
    </Modal>
  );
}
